import { useCallback, useEffect, useRef, useState } from "react";
import { useWebHaptics } from "web-haptics/react";
import type { VideoCategory } from "../content";
import { HAPTIC_ERROR, HAPTIC_LIGHT_NAV, HAPTIC_SUCCESS, HAPTIC_WARNING } from "../hapticPatterns";

type Props = {
  category: VideoCategory;
  onClose: () => void;
  /** First story opened this session — show tap-zone hint once */
  showNavHintOnOpen: boolean;
};

export function StoryViewer({ category, onClose, showNavHintOnOpen }: Props) {
  const { videos } = category;
  const [index, setIndex] = useState(0);
  const [tick, setTick] = useState(0);
  const [showNavHint, setShowNavHint] = useState(showNavHintOnOpen);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trigger } = useWebHaptics();

  const currentSrc = videos[index] ?? "";
  const n = videos.length;

  const bumpTick = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = false;
    v.playsInline = true;
    const onTime = () => bumpTick();
    const onMeta = () => bumpTick();
    const onEnded = () => {
      if (index >= n - 1) {
        void trigger([...HAPTIC_SUCCESS]);
        onClose();
        return;
      }
      setIndex(i => i + 1);
    };

    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("ended", onEnded);

    void v.play().catch(() => {
      /* autoplay policies — user already tapped to open */
    });

    return () => {
      v.pause();
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("ended", onEnded);
    };
  }, [index, n, onClose, bumpTick, currentSrc, trigger]);

  const goNext = useCallback(() => {
    if (index >= n - 1) {
      void trigger([...HAPTIC_SUCCESS]);
      onClose();
      return;
    }
    void trigger([...HAPTIC_LIGHT_NAV]);
    setIndex(i => i + 1);
  }, [index, n, onClose, trigger]);

  const goPrev = useCallback(() => {
    const v = videoRef.current;
    if (index === 0) {
      void trigger([...HAPTIC_WARNING]);
      if (v) {
        v.currentTime = 0;
        void v.play().catch(() => {});
      }
      bumpTick();
      return;
    }
    void trigger([...HAPTIC_LIGHT_NAV]);
    setIndex(i => i - 1);
  }, [index, bumpTick, trigger]);

  const handleClose = useCallback(() => {
    void trigger([...HAPTIC_ERROR]);
    onClose();
  }, [onClose, trigger]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goNext();
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (showNavHint) {
          setShowNavHint(false);
          return;
        }
        void trigger([...HAPTIC_ERROR]);
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose, showNavHint, trigger]);

  const v = videoRef.current;
  const duration = v?.duration && Number.isFinite(v.duration) ? v.duration : 0;
  const currentTime = v?.currentTime ?? 0;
  const segmentProgress = tick >= 0 ? currentTime / (duration || 1) : 0;

  return (
    <div className="story-root" role="dialog" aria-label={`Stories: ${category.title}`}>
      {showNavHint ? (
        <div
          className="story-nav-hint"
          role="presentation"
          onClick={() => setShowNavHint(false)}
        >
          <div className="story-nav-hint-inner">
            <p className="story-nav-hint-title">Navigate</p>
            <p className="story-nav-hint-copy">
              Tap the <strong>left</strong> side to go back, <strong>right</strong> to advance
            </p>
            <p className="story-nav-hint-dismiss">Tap anywhere to dismiss</p>
          </div>
        </div>
      ) : null}

      <div className="story-top">
        <button type="button" className="story-close" onClick={handleClose} aria-label="Close">
          ×
        </button>
        <div className="story-bars" aria-hidden>
          {videos.map((_, i) => {
            let fill = 0;
            if (i < index) fill = 1;
            else if (i === index && duration > 0) fill = Math.min(1, segmentProgress);
            return (
              <div key={i} className="story-bar-track">
                <div className="story-bar-fill" style={{ transform: `scaleX(${fill})` }} />
              </div>
            );
          })}
        </div>
      </div>

      <video
        key={currentSrc}
        ref={videoRef}
        className="story-video"
        src={currentSrc}
        playsInline
        controls={false}
        preload="auto"
      />

      <button type="button" className="story-zone story-zone-left" onClick={goPrev} aria-label="Previous" />
      <button type="button" className="story-zone story-zone-right" onClick={goNext} aria-label="Next" />
    </div>
  );
}
